import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import moment from 'moment';
import { embedSignatureImage, drawSignatureImage } from '../../../../../helpers/embedSignatureImage';
import { fetchPdfBytes } from '../../../../../helpers/fetchPdfBytes';

interface BuildAgreementArgs {
  pdfUrl: string;
  config: any;
  employeeName?: string;
  signaturePng?: string;
  signatureDate?: string;
  documentTitle?: string;
}

const drawSignaturePageBlock = (
  signaturePage: any,
  config: any,
  employeeName: string,
  documentTitle: string,
  fonts: any,
) => {
  const { width } = signaturePage.getSize();
  const ack = config.acknowledgementText;
  if (ack) {
    const text = ack.text.replace('{employeeName}', employeeName).replace('{documentTitle}', documentTitle);
    signaturePage.drawText(text, {
      ...ack,
      font: fonts.helveticaFont,
      maxWidth: width - ack.x * 2,
      lineHeight: 15,
    });
  }
  const lineOptions = { thickness: 0.5, color: rgb(0.2, 0.2, 0.2) };
  const labelOptions = { font: fonts.helveticaObliqueFont, size: 9, color: rgb(0.3, 0.3, 0.3) };
  const block = config.employeeBlock;
  if (!block) return;
  signaturePage.drawText(block.label.text, { ...block.label, ...labelOptions });
  signaturePage.drawLine({ ...block.signature.line, ...lineOptions });
  signaturePage.drawText(block.printedName.label.text, { ...block.printedName.label, ...labelOptions });
  signaturePage.drawLine({ ...block.printedName.line, ...lineOptions });
  signaturePage.drawText(block.date.label.text, { ...block.date.label, ...labelOptions });
  signaturePage.drawLine({ ...block.date.line, ...lineOptions });
};

const drawAddedPageContent = (signaturePage: any, config: any, employeeName: string, dateString: string, font: any) => {
  if (config.employeeBlock?.printedName?.coordinates) {
    signaturePage.drawText(employeeName, {
      ...config.employeeBlock.printedName.coordinates,
      font,
      color: rgb(0, 0, 0),
    });
  }
  if (config.employeeBlock?.date?.coordinates) {
    signaturePage.drawText(dateString, {
      ...config.employeeBlock.date.coordinates,
      font,
      color: rgb(0, 0, 0),
    });
  }
};

const drawExistingPageContent = (
  pages: any[],
  signaturePage: any,
  config: any,
  employeeName: string,
  dateString: string,
  font: any,
) => {
  if (config.coordinatesRectangle) {
    signaturePage.drawRectangle({ ...config.coordinatesRectangle });
  }
  if (config.coordinatesName) {
    const namePage = pages[config.signNamePage || config.signPage || 0];
    namePage.drawText(employeeName, { ...config.coordinatesName, font, color: rgb(0, 0, 0) });
  }
  if (config.coordinatesDate) {
    const datePage = pages[config.signDatePage || config.signPage || 0];
    datePage.drawText(dateString, { ...config.coordinatesDate, font, color: rgb(0, 0, 0) });
  }
  if (config.coordinatesNames) {
    for (const n of config.coordinatesNames) {
      pages[n.page || 0].drawText(employeeName, { ...n.coordinates, font, color: rgb(0, 0, 0) });
    }
  }
};

export const buildAgreementPdf = async ({
  pdfUrl,
  config,
  employeeName = '',
  signaturePng,
  signatureDate,
  documentTitle = 'the document',
}: BuildAgreementArgs): Promise<Uint8Array> => {
  const arrayBuffer = await fetchPdfBytes(pdfUrl);
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);
  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaObliqueFont = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
  const pages = pdfDoc.getPages();
  const fonts = { timesRomanFont, helveticaFont, helveticaObliqueFont };

  let signaturePage;
  if (config.signPage === -1) {
    signaturePage = pdfDoc.addPage();
    pages.push(signaturePage);
    drawSignaturePageBlock(signaturePage, config, employeeName, documentTitle, fonts);
  } else {
    signaturePage = pages[config.signPage || 0];
  }

  if (signaturePage && signaturePng) {
    const png = await embedSignatureImage(pdfDoc, signaturePng);
    const signatureCoords =
      config.employeeBlock?.signature?.coordinates || config.signature?.coordinates || config.coordinates;
    if (signatureCoords) drawSignatureImage(signaturePage, png, signatureCoords);

    const dateString = signatureDate ? moment(signatureDate).format('MM/DD/YYYY') : moment().format('MM/DD/YYYY');
    if (config.signPage === -1) {
      drawAddedPageContent(signaturePage, config, employeeName, dateString, timesRomanFont);
    } else {
      drawExistingPageContent(pages, signaturePage, config, employeeName, dateString, timesRomanFont);
    }
  }

  return pdfDoc.save();
};
